import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent, ThemeEvents } from '@theme/events';

/**
 * A custom element that allows the user to select a quantity.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 *
 * @extends {Component<Refs>}
 */
class QuantitySelectorComponent extends Component {
  /**
   * Handles the quantity increase event.
   * @param {Event} event - The event.
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();

    const { quantityInput } = this.refs;
    const currentValue = this.#getNumericValue(quantityInput.value, 0);
    const step = this.#getStep();
    const max = this.#getMax();

    if (max !== null && currentValue >= max) {
      quantityInput.value = String(max);
      return;
    }

    const nextValue = currentValue + step;
    const safeValue = max !== null ? Math.min(nextValue, max) : nextValue;

    if (safeValue === currentValue) return;

    quantityInput.value = String(safeValue);
    this.#onQuantityChange();
  }

  /**
   * Handles the quantity decrease event.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();

    const { quantityInput } = this.refs;
    const currentValue = this.#getNumericValue(quantityInput.value, 0);
    const step = this.#getStep();
    const min = this.#getMin();

    if (currentValue <= min) {
      quantityInput.value = String(min);
      return;
    }

    const nextValue = currentValue - step;
    const safeValue = Math.max(nextValue, min);

    if (safeValue === currentValue) return;

    quantityInput.value = String(safeValue);
    this.#onQuantityChange();
  }

  /**
   * When our input gets focused, we want to fully select the value.
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    const { quantityInput } = this.refs;
    if (!(event.target instanceof HTMLInputElement) || document.activeElement !== quantityInput) return;

    quantityInput.select();
  }

  /**
   * Handles the quantity set event.
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();

    if (event.target instanceof HTMLInputElement) {
      this.refs.quantityInput.value = event.target.value;
    }

    this.#onQuantityChange();
  }

  /**
   * Handles the quantity change event.
   */
  #onQuantityChange() {
    const { quantityInput } = this.refs;
    const previousValue = this.#getNumericValue(quantityInput.defaultValue || quantityInput.value, 0);

    this.#checkQuantityRules();

    const newValue = this.#getNumericValue(quantityInput.value, 0);
    const cartLine = Number(quantityInput.dataset.cartLine);

    if (newValue === previousValue && quantityInput.dataset.cartLine) {
      return;
    }

    quantityInput.dispatchEvent(new QuantitySelectorUpdateEvent(newValue, cartLine));
  }

  /**
   * Checks the quantity rules are met
   */
  #checkQuantityRules = () => {
    const { quantityInput } = this.refs;

    const min = this.#getMin();
    const max = this.#getMax();
    let newValue = this.#getNumericValue(quantityInput.value, min);

    if (newValue < min) newValue = min;
    if (max !== null && newValue > max) newValue = max;

    quantityInput.value = String(newValue);
  };

  #getNumericValue(value, fallback) {
    const parsed = parseInt(value, 10);
    return Number.isNaN(parsed) ? fallback : parsed;
  }

  #getMin() {
    const { quantityInput } = this.refs;
    return this.#getNumericValue(quantityInput.min, 0);
  }

  #getMax() {
    const { quantityInput } = this.refs;
    if (!quantityInput.max) return null;

    const parsed = parseInt(quantityInput.max, 10);
    return Number.isNaN(parsed) ? null : parsed;
  }

  #getStep() {
    const { quantityInput } = this.refs;
    const parsed = parseInt(quantityInput.step || '1', 10);
    return Number.isNaN(parsed) || parsed < 1 ? 1 : parsed;
  }

  /**
   * Gets the quantity input.
   * @returns {HTMLInputElement} The quantity input.
   */
  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error('Missing <input ref="quantityInput" /> inside <quantity-selector-component />');
    }

    return this.refs.quantityInput;
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define('quantity-selector-component', QuantitySelectorComponent);
}

// Keep CART_COUNT spans in sync with cart updates (e.g., item removal)
document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  const itemCount = /** @type {any} */ (event)?.detail?.data?.itemCount ?? 0;
  try {
    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({ value: String(itemCount), timestamp: Date.now() })
    );
  } catch (_) {}

  const cartCountSpans = document.querySelectorAll('[data-instant-dynamic-content-source="CART_COUNT"]');
  cartCountSpans.forEach((el) => {
    if (el instanceof HTMLElement) {
      el.textContent = itemCount < 100 ? String(itemCount) : '';
      el.style.display = itemCount === 0 ? 'none' : '';
    }
  });
});